/**
 * 🎯 CabinetBids.jsx - TAM PEŞƏKAR VERSİYA
 * 
 * Fayl yeri: src/components/cabinet/CabinetBids.jsx
 * 
 * Xüsusiyyətlər:
 * - ✅ Infinite scroll (lazy loading)
 * - ✅ SignalR real-time updates
 * - ✅ Filter & Sort
 * - ✅ Stats dashboard
 */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { HerracContext } from '../../App';
import { useSignalR } from '../services/SignalRContext';
import useInfiniteScroll from '../useInfiniteScroll';
import {
  FaChartLine,
  FaGavel,
  FaClock,
  FaCheck,
  FaExclamationCircle,
  FaEye,
  FaTrash,
  FaCalendar,
  FaBox,
  FaFilter,
  FaSpinner,
  FaTrophy,
  FaRedo
} from 'react-icons/fa';

const PAGE_SIZE = 12;
const API_URL = 'https://api.herracim.az/api';

function CabinetBids() {
  const navigate = useNavigate();
  const { user } = useContext(HerracContext);
  const { onUserOutbid, onAuctionEnded, onAuctionWon } = useSignalR();

  // State
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    total: 0, winning: 0, outbid: 0, won: 0, expired: 0, active: 0
  });

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Auth check
  useEffect(() => {
    if (!user) navigate('/giris');
  }, [user, navigate]);

  // ════════════════════════════════════════════════════════════
  // FETCH BIDS
  // ════════════════════════════════════════════════════════════
  const fetchBids = useCallback(async (pageNum = 1, reset = false) => {
    if (!user) return;

    try {
      if (reset) { setLoading(true); setError(null); }
      else setLoadingMore(true);

      const params = new URLSearchParams({
        page: pageNum.toString(),
        pageSize: PAGE_SIZE.toString(),
        sortBy: sortBy
      });
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const response = await axios.get(
        `${API_URL}/Bids/my-bids?${params.toString()}`,
        { withCredentials: true }
      );

      const data = response.data;
      const newBids = data.Bids || data.bids || [];

      if (reset) setBids(newBids);
      else setBids(prev => [...prev, ...newBids]);

      setHasMore(data.HasNextPage ?? (newBids.length === PAGE_SIZE));
      setPage(pageNum);
      if (data.Stats) setStats(data.Stats);

    } catch (err) {
      console.error('Təkliflər yüklənərkən xəta:', err);
      setError('Təkliflər yüklənərkən xəta baş verdi');
      if (reset) setBids([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user, filterStatus, sortBy]);

  // İlk yükləmə və filter dəyişiklikləri
  useEffect(() => {
    if (user) {
      setPage(1);
      setHasMore(true);
      fetchBids(1, true);
    }
  }, [user, filterStatus, sortBy]);

  // ════════════════════════════════════════════════════════════
  // INFINITE SCROLL
  // ════════════════════════════════════════════════════════════
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      fetchBids(page + 1, false);
    }
  }, [loadingMore, hasMore, loading, page, fetchBids]);

  useInfiniteScroll(loadMore, 100, 50);

  // ════════════════════════════════════════════════════════════
  // SIGNALR REAL-TIME UPDATES
  // ════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;

    const unsubOutbid = onUserOutbid((data) => {
      setBids(prev => prev.map(bid => {
        if (bid.auctionId === data.productId || bid.auctionId === data.auctionId) {
          return { ...bid, status: 'outbid', isHighestBid: false, currentPrice: data.newBidAmount || data.amount };
        }
        return bid;
      }));
      setStats(prev => ({ ...prev, winning: Math.max(0, prev.winning - 1), outbid: prev.outbid + 1 }));
    });

    const unsubEnded = onAuctionEnded((data) => {
      setBids(prev => prev.map(bid => {
        if (bid.auctionId === data.productId || bid.auctionId === data.auctionId) {
          return { ...bid, status: bid.isHighestBid ? 'won' : 'expired', isActive: false };
        }
        return bid;
      }));
    });

    const unsubWon = onAuctionWon((data) => {
      setBids(prev => prev.map(bid => {
        if (bid.auctionId === data.productId || bid.auctionId === data.auctionId) {
          return { ...bid, status: 'won', isHighestBid: true };
        }
        return bid;
      }));
      setStats(prev => ({ ...prev, winning: Math.max(0, prev.winning - 1), won: prev.won + 1 }));
    });

    return () => { unsubOutbid(); unsubEnded(); unsubWon(); };
  }, [user, onUserOutbid, onAuctionEnded, onAuctionWon]);

  // ════════════════════════════════════════════════════════════
  // DELETE BID
  // ════════════════════════════════════════════════════════════
  const handleDeleteBid = async (bidId) => {
    if (!window.confirm('Bu təklifi silmək istədiyinizə əminsiniz?')) return;

    try {
      await axios.delete(`${API_URL}/Bids/${bidId}`, { withCredentials: true });
      setBids(prev => prev.filter(bid => bid.id !== bidId));
      setStats(prev => ({ ...prev, total: prev.total - 1 }));
    } catch (error) {
      alert(error.response?.data?.Message || 'Təklif silinərkən xəta baş verdi');
    }
  };

  // ════════════════════════════════════════════════════════════
  // HELPERS
  // ════════════════════════════════════════════════════════════
  const getStatusBadge = (status) => {
    const config = {
      winning: { label: '🏆 Qaliblik', color: 'success' },
      won: { label: '✅ Qazandınız', color: 'success' },
      outbid: { label: '📉 Keçildi', color: 'warning' },
      expired: { label: '⏰ Bitdi', color: 'danger' },
      pending: { label: '⏳ Gözləmədə', color: 'info' }
    };
    return config[status] || { label: status || 'Naməlum', color: 'secondary' };
  };

  const getTimeRemaining = (endDate) => {
    if (!endDate) return 'Naməlum';
    const diff = new Date(endDate) - new Date();
    if (diff < 0) return 'Bitdi';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}g ${hours}s`;
    if (hours > 0) return `${hours}s ${minutes}d`;
    return `${minutes}d`;
  };

  const filteredBids = bids.filter((bid) => {
    if (searchTerm && !bid.auctionTitle?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const handleRefresh = () => {
    setPage(1);
    setHasMore(true);
    fetchBids(1, true);
  };

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════
  if (loading && bids.length === 0) {
    return (
      <div className="cabinet-bids">
        <div className="cabinet-bids__loading">
          <FaSpinner className="cabinet-bids__spinner" />
          <p>Təkliflər yüklənir...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cabinet-bids">
      {/* Header */}
      <div className="cabinet-bids__header">
        <div className="cabinet-bids__header-content">
          <h2 className="cabinet-bids__title"><FaChartLine /> Mənim Təkliflərim</h2>
          <p className="cabinet-bids__subtitle">Toplam {stats.total} təklif</p>
        </div>
        <button onClick={handleRefresh} className="cabinet-bids__refresh-btn" title="Yenilə"><FaRedo /></button>
      </div>

      {/* Stats */}
      <div className="cabinet-bids__stats">
        <div className="stat-card">
          <FaGavel className="stat-card__icon" />
          <div className="stat-card__content">
            <span className="stat-card__label">Toplam</span>
            <span className="stat-card__value">{stats.total}</span>
          </div>
        </div>
        <div className="stat-card stat-card--success">
          <FaTrophy className="stat-card__icon" />
          <div className="stat-card__content">
            <span className="stat-card__label">Qaliblik</span>
            <span className="stat-card__value">{stats.winning}</span>
          </div>
        </div>
        <div className="stat-card stat-card--info">
          <FaCheck className="stat-card__icon" />
          <div className="stat-card__content">
            <span className="stat-card__label">Qazanılan</span>
            <span className="stat-card__value">{stats.won}</span>
          </div>
        </div>
        <div className="stat-card stat-card--warning">
          <FaExclamationCircle className="stat-card__icon" />
          <div className="stat-card__content">
            <span className="stat-card__label">Keçilmiş</span>
            <span className="stat-card__value">{stats.outbid}</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="cabinet-bids__toolbar">
        <div className="cabinet-bids__search">
          <input type="text" placeholder="Hərrac adı ilə axtar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="cabinet-bids__search-input" />
        </div>
        <div className="cabinet-bids__controls">
          <button className={`cabinet-bids__filter-btn ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}><FaFilter /> Filtr</button>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="cabinet-bids__sort-select">
            <option value="recent">📅 Ən Yeni</option>
            <option value="oldest">📅 Ən Qədim</option>
            <option value="highest">📈 Yüksək Təklif</option>
            <option value="lowest">📉 Aşağı Təklif</option>
          </select>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="cabinet-bids__filters">
          <div className="filter-group">
            <label>Status</label>
            <div className="filter-options">
              {[
                { key: 'all', label: '📋 Hamısı' },
                { key: 'winning', label: '🏆 Qaliblik' },
                { key: 'outbid', label: '📉 Keçildi' },
                { key: 'won', label: '✅ Qazanılan' },
                { key: 'expired', label: '⏰ Bitmiş' }
              ].map(({ key, label }) => (
                <button key={key} className={`filter-option ${filterStatus === key ? 'active' : ''}`} onClick={() => setFilterStatus(key)}>{label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bids List */}
      {filteredBids.length === 0 ? (
        <div className="cabinet-bids__empty">
          <FaBox className="cabinet-bids__empty-icon" />
          <h3>Təklif tapılmadı</h3>
          <p>{bids.length === 0 ? 'Hələ heç bir təklif verməmisiniz' : 'Bu filtrə uyğun təklif yoxdur'}</p>
        </div>
      ) : (
        <div className="cabinet-bids__list">
          {filteredBids.map((bid) => {
            const statusConfig = getStatusBadge(bid.status);
            const isEnded = bid.status === 'expired' || bid.status === 'won';
            const canDelete = !bid.isHighestBid && bid.status !== 'won';

            return (
              <div key={bid.id} className={`bid-card ${isEnded ? 'bid-card--ended' : ''}`}>
                <div className="bid-card__image">
                  <img src={bid.auctionImage || '/images/placeholder.jpg'} alt={bid.auctionTitle} onError={(e) => { e.target.src = '/images/placeholder.jpg'; }} loading="lazy" />
                  <span className={`bid-card__badge bid-card__badge--${statusConfig.color}`}>{statusConfig.label}</span>
                </div>

                <div className="bid-card__content">
                  <h3 className="bid-card__title">{bid.auctionTitle || 'Hərrac'}</h3>
                  <div className="bid-card__details">
                    <div className="bid-card__detail"><FaChartLine className="bid-card__detail-icon" /><span>Mənim Təklifim:</span><strong>{(bid.bidAmount || 0).toFixed(2)} AZN</strong></div>
                    <div className="bid-card__detail"><FaGavel className="bid-card__detail-icon" /><span>Cari Qiymət:</span><strong>{(bid.currentPrice || 0).toFixed(2)} AZN</strong></div>
                    <div className="bid-card__detail"><FaCalendar className="bid-card__detail-icon" /><span>Tarix:</span><strong>{bid.bidDate ? new Date(bid.bidDate).toLocaleDateString('az-AZ') : '-'}</strong></div>
                    <div className="bid-card__detail"><FaClock className="bid-card__detail-icon" /><span>Qalan Vaxt:</span><strong className={isEnded ? 'text-danger' : 'text-success'}>{getTimeRemaining(bid.auctionEndDate)}</strong></div>
                  </div>

                  {bid.status === 'winning' && <div className="bid-card__alert bid-card__alert--success"><FaTrophy /> Siz bu hərracda qalibsiniz!</div>}
                  {bid.status === 'won' && <div className="bid-card__alert bid-card__alert--success"><FaCheck /> Təbrik edirik! Hərracı qazandınız!</div>}
                  {bid.status === 'outbid' && <div className="bid-card__alert bid-card__alert--warning"><FaExclamationCircle /> Başqa istifadəçi sizi keçdi</div>}
                </div>

                <div className="bid-card__actions">
                  <button className="bid-card__btn bid-card__btn--view" onClick={() => navigate(`/elanlar/herrac/${bid.auctionId}`)} title="Hərracı bax"><FaEye /></button>
                  {canDelete && <button className="bid-card__btn bid-card__btn--delete" onClick={() => handleDeleteBid(bid.id)} title="Təklifi sil"><FaTrash /></button>}
                </div>
              </div>
            );
          })}

          {/* Loading More */}
          {loadingMore && (
            <div className="cabinet-bids__load-more">
              <FaSpinner className="cabinet-bids__spinner" />
              <span>Daha çox yüklənir...</span>
            </div>
          )}

          {/* End of List */}
          {!hasMore && bids.length > 0 && (
            <div className="cabinet-bids__end"><p>Bütün təkliflər yükləndi</p></div>
          )}
        </div>
      )}
    </div>
  );
}

export default CabinetBids;